import { supabase } from "@/lib/supabase";

/**
 * ISMAN Forms — every website form is stored in the ISMAN admin database
 * (table: form_submissions) and appears at admin.isman.co.ke/export.
 * No third-party (Readdy) form endpoints are used.
 */

// Short prefix per form, used in the reference code (e.g. ISM-ENQ-261014-7K3Q)
export const FORM_CODES: Record<string, string> = {
  "general-enquiry": "ENQ",
  "commercial-kitchen": "KIT",
  "food-processing": "FDP",
  "hospital-pharmacy": "HSP",
  "hvac-systems": "HVC",
  "design-services": "DSN",
  "plumbing-services": "PLB",
  "stainless-railing": "RLG",
  "welding-services": "WLD",
  "store-order": "ORD",
};

const ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ"; // no 0/O/1/I confusion

/** Generate a unique, human-friendly reference code: ISM-<TYPE>-<YYMMDD>-<XXXX> */
export function generateFormCode(formSlug: string): string {
  const prefix = FORM_CODES[formSlug] || "FRM";
  const d = new Date();
  const date =
    String(d.getFullYear()).slice(2) +
    String(d.getMonth() + 1).padStart(2, "0") +
    String(d.getDate()).padStart(2, "0");
  const bytes = new Uint8Array(4);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < 4; i++) bytes[i] = Math.floor(Math.random() * 256);
  const rand = Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join("");
  return `ISM-${prefix}-${date}-${rand}`;
}

type FormInput = URLSearchParams | FormData | Record<string, string>;

function toValues(input: FormInput): Record<string, string> {
  const values: Record<string, string> = {};
  if (input instanceof URLSearchParams || input instanceof FormData) {
    input.forEach((v, k) => {
      if (typeof v === "string") values[k] = v;
    });
  } else {
    Object.assign(values, input);
  }
  return values;
}

export interface SubmitResult {
  ok: boolean;
  code: string;
  error?: string;
}

/**
 * Save a form submission to the ISMAN admin database.
 * Returns the reference code to show the customer.
 */
export async function submitForm(
  formSlug: string,
  input: FormInput,
  opts: { code?: string } = {},
): Promise<SubmitResult> {
  const code = opts.code || generateFormCode(formSlug);
  const values = {
    reference: code,
    form_type: formSlug,
    ...toValues(input),
  };
  const page_slug =
    (typeof window !== "undefined" ? window.location.pathname.replace(/^\/+|\/+$/g, "") : "") || "home";

  let result: SubmitResult;
  // 1) ISMAN Forms endpoint on our own server (shown in admin → Submitted Forms)
  try {
    const r = await fetch("/api/forms/submit.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ form_slug: formSlug, page_slug, code, values }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; code?: string; error?: string };
    result = r.ok && j.ok ? { ok: true, code: j.code || code } : { ok: false, code, error: j.error || `HTTP ${r.status}` };
  } catch (e) {
    result = { ok: false, code, error: e instanceof Error ? e.message : "Network error" };
  }

  // 2) Backup copy in the database inbox (never blocks the customer)
  try {
    const v = values as Record<string, string>;
    const pick = (keys: string[]) =>
      Object.keys(v).find((k) => keys.some((x) => k.toLowerCase().includes(x)));
    const details = Object.entries(v)
      .filter(([, val]) => val)
      .map(([k, val]) => `${k.replace(/_/g, " ")}: ${val}`)
      .join("\n");
    const { error } = await supabase.from("contact_submissions").insert({
      name: v[pick(["name"]) || ""] || "",
      email: v[pick(["email"]) || ""] || "not-provided@isman.co.ke",
      phone: v[pick(["phone", "mobile", "tel"]) || ""] || "",
      service: `[${result.code}] ${v[pick(["service", "items", "system", "type"]) || ""] || formSlug}`.slice(0, 250),
      message: details.slice(0, 5000),
      status: "new",
    });
    if (!result.ok && !error) result = { ok: true, code };
  } catch {
    /* backup is best-effort */
  }

  showReferenceNotice(result, values);
  return result;
}

const WHATSAPP = "254724114555";

/** Small self-contained notice showing the customer their reference code.
 *  If saving failed, offers a one-tap WhatsApp fallback so no lead is lost. */
function showReferenceNotice(res: SubmitResult, values: Record<string, string>) {
  if (typeof document === "undefined") return;
  document.getElementById("isman-form-notice")?.remove();
  const box = document.createElement("div");
  box.id = "isman-form-notice";
  box.setAttribute("role", "status");
  box.style.cssText =
    "position:fixed;z-index:9999;left:50%;bottom:24px;transform:translateX(-50%);max-width:92vw;width:420px;" +
    "background:#0f172a;color:#fff;border-radius:14px;padding:16px 18px;box-shadow:0 12px 40px rgba(0,0,0,.35);" +
    "font-family:inherit;font-size:14px;line-height:1.45;border-top:4px solid " + (res.ok ? "#0d9488" : "#f59e0b");

  const summary = Object.entries(values)
    .filter(([k, v]) => v && k !== "form_type")
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
    .join("\n");
  const wa = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(summary)}`;

  box.innerHTML = res.ok
    ? `<div style="font-weight:700;margin-bottom:4px">✓ Received — thank you!</div>
       <div style="opacity:.8">Your reference code:</div>
       <div style="display:flex;gap:8px;align-items:center;margin-top:6px">
         <code style="font-size:16px;font-weight:700;letter-spacing:.5px;background:#134e4a;padding:6px 10px;border-radius:8px">${res.code}</code>
         <button data-copy style="background:#0d9488;color:#fff;border:0;border-radius:8px;padding:6px 10px;cursor:pointer">Copy</button>
       </div>
       <div style="opacity:.7;font-size:12px;margin-top:8px">Quote this code when you call or WhatsApp us.</div>`
    : `<div style="font-weight:700;margin-bottom:4px">We couldn't send your form online</div>
       <div style="opacity:.8">Tap below to send the same details on WhatsApp (ref <b>${res.code}</b>).</div>
       <a href="${wa}" target="_blank" rel="noopener" style="display:inline-block;margin-top:10px;background:#16a34a;color:#fff;padding:8px 14px;border-radius:8px;text-decoration:none;font-weight:600">Send via WhatsApp</a>`;

  const close = document.createElement("button");
  close.textContent = "×";
  close.setAttribute("aria-label", "Close");
  close.style.cssText = "position:absolute;top:6px;right:10px;background:none;border:0;color:#fff;font-size:20px;cursor:pointer;opacity:.7";
  close.onclick = () => box.remove();
  box.appendChild(close);
  box.querySelector<HTMLButtonElement>("[data-copy]")?.addEventListener("click", (e) => {
    navigator.clipboard?.writeText(res.code);
    (e.currentTarget as HTMLButtonElement).textContent = "Copied";
  });
  document.body.appendChild(box);
  if (res.ok) setTimeout(() => box.remove(), 20000);
}
